import {isFeatureEnabled} from 'feature-flags';
import {
  ChipsOption,
  FormControl,
  FormField,
  Label,
  Separator,
  Switch,
  Tooltip,
} from 'platform/components';
import {
  Grid,
  GridItem,
  Hide,
  HStack,
  Icon,
  Link,
  Show,
  Space,
  Text,
  useDevice,
  VStack,
} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil, sort} from 'ramda';

import {suffixTestId, TestIdProps, useQueryState} from 'shared/web';

import {NO_TRIM_LEVEL_OPTION} from '../../constants/noTrimLevelOption';
import {isTransmissionExcluded} from '../../constants/transmissionOptionsExcluded';
import {useFilter} from '../../hooks/FilterContext';
import {useCatalogue} from '../../hooks/useCatalogue';
import {useTrimLevelsCatalog} from '../../hooks/useTrimLevelsCatalog';
import i18n from '../../i18n/i18n';
import {AdServer} from '../../types/AdServer';
import {FilterFormData} from '../../types/FilterFormData';
import {PriceReportType} from '../../types/PriceReportType';
import {featureFlags} from '../../utils/featureFlags';
import {getCurrencyCodeFromString} from '../../utils/getCurrencyCodeFromString';
import {getDaysInAdvertisementOptions} from '../../utils/getDaysInAdvertisementOptions';
import {getFeatureScoreOptions} from '../../utils/getFeatureScoreOptions';
import {getPowerOptions} from '../../utils/getPowerOptions';
import {getPriceOptions} from '../../utils/getPriceOptions';
import {getYearOptions} from '../../utils/getYearOptions';
import {isUnsupportedCombination} from '../../utils/isUnsupportedCombination';
import {mapMarketToAdServer} from '../../utils/mapMarketToAdServer';
import {getMileageOptions} from '../../utils/mileageOptions';
import {QueryStateKeys} from '../../utils/routing';
import {AddressSearch} from './components/AddressSearch';
import {AdServerFilterSection} from './components/AdServerFilterSection';
import {FeaturesFilterField} from './components/FeaturesFilterField/FeaturesFilterField';
import {Search} from './components/Search';
import {getOptionsWithPriority} from './utils/getOptionsWithPriority';

const FUEL_OPTIONS_SHOWN = 2;

export interface FilterProps extends TestIdProps {
  priceReport: PriceReportType;
  formControl: FormControl<FilterFormData>;
  formApi: UseFormReturn<FilterFormData>;
  hasDefaultPreset?: boolean;
  isResetDisabled?: boolean;
  onReset?: () => void;
}

export function Filter(props: FilterProps) {
  const {driveOptions, fuelOptions, styleOptions, transmissionOptions, sellerTypeOptions} =
    useCatalogue();
  const {filter, errors} = useFilter();
  const [debug, setDebug] = useQueryState(QueryStateKeys.Debug);
  const device = useDevice();
  const driveOptionsWithPriority = getOptionsWithPriority(driveOptions, filter?.drive);
  const chipsSize = device === 'mobile' || device === 'tablet' ? 'default' : 'small';
  const isFromCebia =
    props.priceReport.serviceCode === 'cebia' || props.priceReport.serviceCode === 'cebia-dev';

  const showStockCars = useWatch({control: props.formApi.control, name: 'showStockCars'});
  const showSoldIn30 = useWatch({control: props.formApi.control, name: 'showSoldIn30'});
  const showSoldIn90 = useWatch({control: props.formApi.control, name: 'showSoldIn90'});
  const priceGte = useWatch({control: props.formApi.control, name: 'priceGte'});
  const priceLte = useWatch({control: props.formApi.control, name: 'priceLte'});
  const firstRegistrationOnYearGte = useWatch({
    control: props.formApi.control,
    name: 'firstRegistrationOnYearGte',
  });
  const firstRegistrationOnYearLte = useWatch({
    control: props.formApi.control,
    name: 'firstRegistrationOnYearLte',
  });
  const mileageGte = useWatch({control: props.formApi.control, name: 'mileageGte'});
  const mileageLte = useWatch({control: props.formApi.control, name: 'mileageLte'});
  const daysInAdvertisementGte = useWatch({
    control: props.formApi.control,
    name: 'daysInAdvertisementGte',
  });
  const daysInAdvertisementLte = useWatch({
    control: props.formApi.control,
    name: 'daysInAdvertisementLte',
  });
  const powerGte = useWatch({control: props.formApi.control, name: 'powerGte'});
  const powerLte = useWatch({control: props.formApi.control, name: 'powerLte'});
  const featureScoreGte = useWatch({control: props.formApi.control, name: 'featureScoreGte'});
  const featureScoreLte = useWatch({control: props.formApi.control, name: 'featureScoreLte'});
  const server = useWatch({control: props.formApi.control, name: 'server'});

  const isShowStockCarsDisabled = isUnsupportedCombination(
    !showStockCars,
    showSoldIn30,
    showSoldIn90
  );
  const isShowSoldIn30Disabled = isUnsupportedCombination(
    showStockCars,
    !showSoldIn30,
    showSoldIn90
  );
  const isShowSoldIn90Disabled = isUnsupportedCombination(
    showStockCars,
    showSoldIn30,
    !showSoldIn90
  );

  const yesNoOptions: ChipsOption[] = [
    {label: i18n.t('general.labels.yes'), value: 'YES'},
    {label: i18n.t('general.labels.no'), value: 'NO'},
  ];

  const transmissionOptionsSupported = transmissionOptions?.filter(
    (option) => !isTransmissionExcluded(option.value)
  );
  const vehicleFuelOptionVisible =
    (fuelOptions?.findLastIndex((option) => filter?.fuelType?.includes(option.value)) ?? 0) <
    FUEL_OPTIONS_SHOWN;
  const sellerTypeOptionsVisible =
    sellerTypeOptions?.filter((option) =>
      ['SELLERTYPE_PRIVATE_SELLER', 'SELLERTYPE_DEALERSHIP'].includes(option.value)
    ) ?? [];

  const {catalog: trimLevelsCatalog, hasTrimLevels} = useTrimLevelsCatalog(
    props.priceReport,
    firstRegistrationOnYearGte,
    firstRegistrationOnYearLte
  );
  const trimLevelsOptions = [
    {
      label: i18n.t('entity.filterForm.labels.noTrimLevel'),
      value: NO_TRIM_LEVEL_OPTION,
    },
    ...sort(
      (a, b) => {
        if (isNotNil(a.label) && isNotNil(b.label)) {
          return a.label.localeCompare(b.label);
        }
        return 0;
      },
      trimLevelsCatalog?.map((trimLevel) => ({
        label: trimLevel.label,
        value: trimLevel.const_key,
      })) ?? []
    ),
  ];

  const adServers = mapMarketToAdServer(props.priceReport.market).slice(
    0,
    !isFeatureEnabled(featureFlags.PR_MULTI_SERVERS) ? 1 : undefined
  );
  const onServerSwitchChange = (adServer: AdServer, value: boolean) => {
    if (value) {
      props.formApi.setValue('server', [...(server ?? []), adServer]);
    } else {
      props.formApi.setValue(
        'server',
        server?.filter((s) => s !== adServer)
      );
    }
    props.formControl.onChange();
  };

  return (
    <VStack spacing={4}>
      <Hide when={isFromCebia}>
        <VStack>
          <Label tooltip={i18n.t('entity.filterForm.advertisementServerTooltip')}>
            {i18n.t('entity.filterForm.advertisementServer')}
          </Label>
          <Space vertical={2} />
          <VStack spacing={4}>
            <AdServerFilterSection
              adServers={adServers}
              server={server}
              priceReportId={props.priceReport.id}
              onServerSwitchChange={onServerSwitchChange}
              data-testid={suffixTestId('adServerFilter', props)}
            />
          </VStack>
        </VStack>
      </Hide>

      <VStack>
        <Label>{i18n.t('entity.filterForm.labels.title')}</Label>
        <Search
          name="title"
          control={props.formControl}
          placeholder={i18n.t('entity.filterForm.placeholders.title')}
        />
      </VStack>

      <AddressSearch
        name="address"
        control={props.formControl}
        data-testid={suffixTestId('address', props)}
      />
      <FormField
        control={props.formControl}
        name="radius"
        type="numberInputWithOptions"
        label={`${i18n.t('entity.filterForm.radius')} (km)`}
        placeholder={i18n.t('entity.filterForm.placeholders.radius')}
        options={[5, 10, 20, 50, 100]}
        errorMessage={errors?.radius}
        data-testid={suffixTestId('radius', props)}
        isClearable
      />

      <Tooltip label={getTooltipLabel(isShowStockCarsDisabled)} placement="top-start">
        <FormField
          control={props.formControl}
          name="showStockCars"
          type="switch"
          label={i18n.t('entity.filterForm.labels.showStockCars')}
          isDisabled={isShowStockCarsDisabled}
          data-testid={suffixTestId('showStockCars', props)}
        />
      </Tooltip>
      <Tooltip label={getTooltipLabel(isShowSoldIn30Disabled)} placement="top-start">
        <FormField
          control={props.formControl}
          name="showSoldIn30"
          type="switch"
          label={i18n.t('entity.filterForm.labels.showSoldIn30')}
          isDisabled={isShowSoldIn30Disabled}
          data-testid={suffixTestId('showSoldIn30', props)}
        />
      </Tooltip>
      <Tooltip label={getTooltipLabel(isShowSoldIn90Disabled)} placement="top-start">
        <FormField
          control={props.formControl}
          name="showSoldIn90"
          type="switch"
          label={i18n.t('entity.filterForm.labels.showSoldIn90')}
          isDisabled={isShowSoldIn90Disabled}
          data-testid={suffixTestId('showSoldIn90', props)}
        />
      </Tooltip>
      <HStack align="center" spacing={1}>
        <Switch
          value={debug === 'true'}
          onChange={(value) => setDebug(value ? 'true' : 'false')}
          label={i18n.t('entity.filterForm.labels.showDebugCars')}
          data-testid={suffixTestId('showDebugCars', props)}
        />
        <Tooltip description={i18n.t('entity.filterForm.labels.debugCarsTooltip')}>
          <Icon value="action/help" color="text.secondary" size={4} />
        </Tooltip>
      </HStack>

      <Grid columns={2} spacing={2} align="flex-end">
        <FormField
          control={props.formControl}
          name="priceGte"
          type="numberInputWithOptions"
          label={`${i18n.t('entity.filterForm.price')} (${
            props.priceReport.sellingPrice.recommended.currency ?? ''
          })`}
          placeholder={i18n.t('entity.filterForm.from')}
          options={getPriceOptions(
            getCurrencyCodeFromString(props.priceReport.sellingPrice.recommended.currency),
            null,
            priceLte
          )}
          data-testid={suffixTestId('priceGte', props)}
          isInvalid={!!errors?.price}
          isClearable
        />

        <FormField
          control={props.formControl}
          name="priceLte"
          type="numberInputWithOptions"
          placeholder={i18n.t('entity.filterForm.to')}
          options={getPriceOptions(
            getCurrencyCodeFromString(props.priceReport.sellingPrice.recommended.currency),
            priceGte,
            null
          )}
          data-testid={suffixTestId('priceLte', props)}
          isInvalid={!!errors?.price}
          isClearable
        />

        <Show when={!!errors?.price}>
          <GridItem span={2}>
            <Text size="small" color="danger" data-testid={suffixTestId('priceError', props)}>
              {errors?.price}
            </Text>
          </GridItem>
        </Show>
      </Grid>

      <FormField
        control={props.formControl}
        name="isVatDeductible"
        label={i18n.t('entity.filterForm.VATDeductible')}
        type="chips"
        size={chipsSize}
        options={yesNoOptions}
        isMultiple
        data-testid={suffixTestId('isVatDeductible', props)}
      />

      <Grid columns={2} spacing={2} align="flex-end">
        <FormField
          control={props.formControl}
          name="firstRegistrationOnYearGte"
          type="choice"
          label={i18n.t('entity.filterForm.registration')}
          placeholder={i18n.t('entity.filterForm.from')}
          options={getYearOptions(null, firstRegistrationOnYearLte)}
          data-testid={suffixTestId('yearGte', props)}
        />

        <FormField
          control={props.formControl}
          name="firstRegistrationOnYearLte"
          type="choice"
          placeholder={i18n.t('entity.filterForm.to')}
          options={getYearOptions(firstRegistrationOnYearGte)}
          data-testid={suffixTestId('yearLte', props)}
        />
      </Grid>

      <Grid columns={2} spacing={2} align="flex-end">
        <FormField
          control={props.formControl}
          name="mileageGte"
          type="numberInputWithOptions"
          label={`${i18n.t('entity.filterForm.millage')} (km)`}
          placeholder={i18n.t('entity.filterForm.from')}
          options={getMileageOptions(null, mileageLte)}
          data-testid={suffixTestId('mileageGte', props)}
          isInvalid={!!errors?.mileage}
          isClearable
        />

        <FormField
          control={props.formControl}
          name="mileageLte"
          type="numberInputWithOptions"
          placeholder={i18n.t('entity.filterForm.to')}
          options={getMileageOptions(mileageGte)}
          data-testid={suffixTestId('mileageLte', props)}
          isInvalid={!!errors?.mileage}
          isClearable
        />

        <Show when={!!errors?.mileage}>
          <GridItem span={2}>
            <Text size="small" color="danger" data-testid={suffixTestId('mileageError', props)}>
              {errors?.mileage}
            </Text>
          </GridItem>
        </Show>
      </Grid>

      <FormField
        control={props.formControl}
        name="transmission"
        label={i18n.t('entity.filterForm.transmission')}
        type="chips"
        size={chipsSize}
        options={(transmissionOptionsSupported ?? []) as ChipsOption[]}
        isMultiple
        data-testid={suffixTestId('transmission', props)}
      />

      <FormField
        label={i18n.t('entity.filterForm.fuel')}
        control={props.formControl}
        name="fuelType"
        type="chips"
        size={chipsSize}
        limit={vehicleFuelOptionVisible ? FUEL_OPTIONS_SHOWN : undefined}
        options={(fuelOptions ?? []) as ChipsOption[]}
        isMultiple
        data-testid={suffixTestId('fuelType', props)}
      />

      <Grid columns={2} spacing={2} align="flex-end">
        <FormField
          control={props.formControl}
          name="powerGte"
          type="numberInputWithOptions"
          label={`${i18n.t('entity.filterForm.power')} (kW)`}
          placeholder={i18n.t('entity.filterForm.from')}
          options={getPowerOptions(null, powerLte)}
          data-testid={suffixTestId('powerGte', props)}
          isInvalid={!!errors?.power}
          isClearable
        />

        <FormField
          control={props.formControl}
          name="powerLte"
          type="numberInputWithOptions"
          placeholder={i18n.t('entity.filterForm.to')}
          options={getPowerOptions(powerGte)}
          data-testid={suffixTestId('powerLte', props)}
          isInvalid={!!errors?.power}
          isClearable
        />

        <Show when={!!errors?.power}>
          <GridItem span={2}>
            <Text size="small" color="danger">
              {errors?.power}
            </Text>
          </GridItem>
        </Show>
      </Grid>

      {/* menuInPortal attr in FormField is needed, because otherwise the menu
          is inside the StickyContainer, whose position is sticky when
          scrolling up, and relative components can cover it */}
      <FormField
        control={props.formControl}
        name="bodyStyle"
        type="multiChoice"
        label={i18n.t('entity.filterForm.body')}
        options={styleOptions ?? []}
        menuInPortal
        data-testid={suffixTestId('bodyStyle', props)}
      />

      <FormField
        control={props.formControl}
        name="drive"
        type="chips"
        size={chipsSize}
        label={i18n.t('entity.filterForm.propulsion')}
        limit={driveOptionsWithPriority.length > 2 ? 2 : undefined}
        options={driveOptions ?? []}
        isMultiple
        data-testid={suffixTestId('drive', props)}
      />

      <FormField
        control={props.formControl}
        name="trimLevels"
        type="multiChoice"
        label={i18n.t('entity.filterForm.featureTiers')}
        options={trimLevelsOptions ?? []}
        menuInPortal
        menuPlacement="top"
        isDisabled={!hasTrimLevels}
        data-testid={suffixTestId('trimLevels', props)}
      />

      <FeaturesFilterField
        formControl={props.formControl}
        formApi={props.formApi}
        data-testid={suffixTestId('features', props)}
      />

      <Grid columns={2} spacing={2} align="flex-end">
        <FormField
          control={props.formControl}
          name="featureScoreGte"
          type="numberInputWithOptions"
          label={`${i18n.t('entity.filterForm.featureScore')} (%)`}
          placeholder={i18n.t('entity.filterForm.from')}
          options={getFeatureScoreOptions(null, featureScoreLte)}
          data-testid={suffixTestId('featureScoreGte', props)}
          isInvalid={!!errors?.featureScore}
          isClearable
        />

        <FormField
          control={props.formControl}
          name="featureScoreLte"
          type="numberInputWithOptions"
          placeholder={i18n.t('entity.filterForm.to')}
          options={getFeatureScoreOptions(featureScoreGte)}
          data-testid={suffixTestId('featureScoreLte', props)}
          isInvalid={!!errors?.featureScore}
          isClearable
        />

        <Show when={!!errors?.featureScore}>
          <GridItem span={2}>
            <Text size="small" color="danger">
              {errors?.featureScore}
            </Text>
          </GridItem>
        </Show>
      </Grid>

      <Show whenFeatureEnabled={featureFlags.PR_FILTERS_DAYS_ON_STOCK}>
        <Grid columns={2} spacing={2} align="flex-end">
          <FormField
            control={props.formControl}
            name="daysInAdvertisementGte"
            type="numberInputWithOptions"
            label={i18n.t('entity.filterForm.daysInAdvertisement')}
            placeholder={i18n.t('entity.filterForm.from')}
            options={getDaysInAdvertisementOptions(null, daysInAdvertisementLte)}
            data-testid={suffixTestId('daysInAdvertisementGte', props)}
            isInvalid={!!errors?.daysInAdvertisement}
            isClearable
          />

          <FormField
            control={props.formControl}
            name="daysInAdvertisementLte"
            type="numberInputWithOptions"
            placeholder={i18n.t('entity.filterForm.to')}
            options={getDaysInAdvertisementOptions(daysInAdvertisementGte)}
            data-testid={suffixTestId('daysInAdvertisementLte', props)}
            isInvalid={!!errors?.daysInAdvertisement}
            isClearable
          />

          <Show when={!!errors?.daysInAdvertisement}>
            <GridItem span={2}>
              <Text
                size="small"
                color="danger"
                data-testid={suffixTestId('daysInAdvertisementError', props)}
              >
                {errors?.daysInAdvertisement}
              </Text>
            </GridItem>
          </Show>
        </Grid>
      </Show>

      <Show whenFeatureEnabled={featureFlags.PR_FILTERS_SELLER_TYPE}>
        <FormField
          control={props.formControl}
          name="sellerType"
          label={i18n.t('entity.table.sellerType')}
          type="chips"
          size={chipsSize}
          options={sellerTypeOptionsVisible}
          isMultiple
          data-testid={suffixTestId('sellerType', props)}
        />
      </Show>

      <Show when={isNotNil(props.onReset)}>
        <Separator spacing={1} />
        <Link
          size="small"
          leftIcon="navigation/refresh"
          onClick={props.onReset}
          isDisabled={props.isResetDisabled}
          title={i18n.t(
            props.hasDefaultPreset
              ? 'entity.filterForm.restoreDefaultSettings'
              : 'entity.filterForm.restoreOriginalSettings'
          )}
          data-testid={suffixTestId('reset', props)}
        />
      </Show>
    </VStack>
  );
}

const getTooltipLabel = (isDisabled: boolean) =>
  isDisabled ? i18n.t('entity.filterForm.labels.unsupportedCombination') : undefined;
